import { useCallback, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { QueryKeys } from '@gokrew/models/constants';
import { createDesignation, getDesignationList } from '@/api/designation';
import { useAuth } from '@/context/AuthContext';
import { useLinkedBranches } from '@/hooks/branches/useBranches';
import type { HireJobPrefill } from '@/types/employee';
import type { JobPrefillOptions } from '@/pages/employees/JobPrefillSeed';
import {
  hireJobTitle,
  NEW_DESIGNATION_VALUE,
} from '@/pages/employees/hireJobPrefill';
import { matchOrgUnitByName } from '@/utils/orgUnitMatch';

type SetFieldValue = (field: string, value: unknown) => unknown;

/** A designation's id by its exact name (any case, accents, spacing). */
const findDesignationId = async (companyId: string, name: string) => {
  const { data } = await getDesignationList(companyId, { name, limit: 100 });
  return matchOrgUnitByName(data?.data ?? [], [name])?.id;
};

/**
 * The Add employee form's side of a hire's Job Title and Department prefill:
 * the options `JobPrefillSeed` picked (seeded into the pickers so they show
 * even off their first page), and `resolveDesignationId`, which creates the
 * designation on save when the Job Title stands for a new one.
 */
export const useHireJobPrefill = (job: HireJobPrefill | undefined) => {
  const { companyId, branchId: authBranchId, selectedCompany } = useAuth();
  const companyLanguage = selectedCompany?.language;
  const { data: branches } = useLinkedBranches();
  const queryClient = useQueryClient();
  const [picked, setPicked] = useState<JobPrefillOptions>({});

  const onSeeded = useCallback(
    (options: JobPrefillOptions) => setPicked(options),
    []
  );
  const designationSeeds = useMemo(
    () => (picked.designation ? [picked.designation] : undefined),
    [picked.designation]
  );
  const departmentSeeds = useMemo(
    () => (picked.department ? [picked.department] : undefined),
    [picked.department]
  );

  /**
   * The designation id to save the employee with. For a new Job Title, it is
   * created first, in every linked branch like the Job Title dialog does by
   * default; the form then holds its id, so a retry after a failed employee
   * save does not create it twice.
   */
  const resolveDesignationId = useCallback(
    async (
      designationId: string | undefined,
      employeeBranchId: string | undefined,
      setFieldValue: SetFieldValue
    ) => {
      if (designationId !== NEW_DESIGNATION_VALUE) return designationId;
      const linked = branches?.data?.map((branch) => branch.id) ?? [];
      const fallback = employeeBranchId || authBranchId;
      const branchIds = [
        ...new Set([
          ...linked,
          ...(employeeBranchId ? [employeeBranchId] : []),
          ...(!linked.length && fallback ? [fallback] : []),
        ]),
      ];
      const name = job ? hireJobTitle(job, companyLanguage) : '';
      const { data: created } = await createDesignation(companyId!, {
        name,
        branchIds,
      });
      void queryClient.invalidateQueries({
        queryKey: [QueryKeys.Designations],
      });
      // The create endpoint can answer without the new row (it reads it back
      // before its transaction commits), so find it by name then.
      const id = created?.id ?? (await findDesignationId(companyId!, name));
      if (!id) return undefined;
      setPicked((current) => ({
        ...current,
        designation: { value: id, label: name },
      }));
      void setFieldValue('designationId', id);
      return id;
    },
    [branches?.data, authBranchId, companyId, job, companyLanguage, queryClient]
  );

  return { onSeeded, designationSeeds, departmentSeeds, resolveDesignationId };
};
